import * as _ from 'lodash'
import util from './util';
import serviceGateway from './serviceGateway';
import store, { DomainEntityType, getCollection, getDomainEntities, getModel, ModelCollection } from './store';
import * as globalActions from './actions';
import enums from './enum';
import jobModel from './models/jobModel';
import jobCostModel from './models/jobCostModel';
import socketClient from './socketClient';
import globalRelay from './globalRelay';
import { getInstallSheets } from './schemaHelper';

export const socketClientHandlers = {
	['model.jobcost.added']: message => {
		const jobCostKey = message.payload.key;
		const model = store().get('model');
		const jobs = model.get('jobs');
		const contractors = model.get('contractors');

		if (jobs.some(j => j.jobCostKeys.indexOf(jobCostKey) !== -1) || contractors.some(c => c.jobCostKeys.indexOf(jobCostKey) !== -1)) {
			fetchJobCost({ key: jobCostKey });
		}
	},
	['model.jobcost.updated']: message => {
		const keyPath = ['model', 'jobCosts', message.payload.key];
		const existing = store().getIn(keyPath);

		if (existing) {
			if (existing.itemVersion === message.payload.patchTargetVersion) {
				existing.applyPatch(message.payload.patch);
				store().setIn(keyPath, existing);
			}
			else if (existing.itemVersion < message.payload.patchTargetVersion) {
				fetchJobCost({ key: message.payload.key });
			}
		}
	}
};

// wrap handlers to allow extensions to override
for (const key of _.keys(socketClientHandlers)) {
	socketClient.on(key, message => {
		socketClientHandlers[key](message);
	});
}

export function ensureUniqueJobCostName (jobCost) {
	if (!jobCost.jobKey && !jobCost.contractorKey) { return; }

	const nameFilter = name => {
		return jc => {
			const key = jc.jobKey ? 'jobKey' : jc.contractorKey ? 'contractorKey' : 'invalid';

			return jc.key !== jobCost.key && jc[key] === jobCost[key] && jc.name === name;
		};
	};

	const jobCosts = getCollection({ collection: ModelCollection.jobCosts });

	if (jobCosts.filter(nameFilter(jobCost.name)).count()) {
		const rootName = jobCost.name.replace(/\s\d+$/, '');
		let nameSuffix = 1;
		let newName;
		while (!newName || jobCosts.filter(nameFilter(newName)).count() !== 0) { //eslint-disable-line  no-loop-func
			newName = `${rootName} ${nameSuffix}`;
			nameSuffix++;
		}

		jobCost.name = newName;
	}
}

// Global handlers
export const globalHandlers = {
	[globalRelay.JOB_COST_CREATE]: (jobCost) => {
		// if no variant has been set, use the default variant
		if (!jobCost.companyVariant) { jobCost.companyVariant = globalRelay.config!.variant; }

		const storeInstance = store();
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);
		const installSettings = globalConfig.jobCostSettings.installation;
		const job = getModel({ store: storeInstance, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const contractor = getModel({ store: storeInstance, collection: ModelCollection.contractors, key: jobCost.contractorKey });
		const builder = job && job.builderKey && getModel({ store: storeInstance, collection: ModelCollection.contractors, key: job.builderKey });
		const currentModule = storeInstance.getIn(['viewState', 'currentModule']);

		if (!jobCost.installationMode) { jobCost.installationMode = installSettings.mode; }

		if (job || ['retail-sales', 'builder-sales'].indexOf(currentModule) !== -1) {
			const businessSegmentName = job ? enums.businessSegments.getName(job.businessSegment).toLowerCase() : currentModule.split('-')[0];
			const segmentKey = (job && job.businessSegment === enums.businessSegments.Retail && job.isRenovation) ? `${businessSegmentName}Reno` : businessSegmentName;

			if (jobCost.installationMode !== 'none') {
				jobCost.installationUnitPrice = installSettings.unitPrice[segmentKey];
			}
		}

		if (job || contractor) {
			// be sure not to create a duplicate name (will need to re-evaluate when builder is implemented)
			ensureUniqueJobCostName(jobCost);
		}

		if (jobCost.installationMode !== 'none') {
			jobCost.setSetting(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined, globalActions.formatConfigName(jobCost));

			if (typeof jobCost.allowInstallSheetChanges === 'undefined') { jobCost.allowInstallSheetChanges = installSettings.sheets.allowChanges; }

			if (getInstallSheets(jobCost).length() === 0) {
				let sheetIdx = 0;

				_.forEach(installSettings.sheets.defaults, d => {
					jobCost.installSheets.add({ jobCostKey: jobCost.key, margin: jobCost.margin, name: d, sortIndex: sheetIdx++, unitCost: jobCost.installationUnitPrice });
				});
			}
		}

		const workAreaSpecVersion = globalConfig.jobCostSettings.specifications.workAreaSpecVersion;
		const workAreaConfigName = globalConfig.jobCostSettings.specifications.useVariantWorkAreaConfig ? `workarea-specs-${jobCost.companyVariant}-${workAreaSpecVersion}` : `workarea-specs-${workAreaSpecVersion}`;
		jobCost.setSetting(enums.jobCostSettingGroups.General, 'workAreaSpecDefinition', undefined, workAreaConfigName);
		jobCost.setSetting(enums.jobCostSettingGroups.General, 'deliveryConfigVersion', undefined, globalConfig.jobCostSettings.deliveryConfigVersion);

		if (jobCost.margin) { jobCost.installationMargin = jobCost.margin; }

		if (contractor || builder) {
			// the contractor is the parent of a template, the builder may be assigned to a job
			const discountPercent = contractor ? contractor.discountPercent : builder && builder.discountPercent;

			jobCost.setSetting(enums.jobCostSettingGroups.General, 'builderDiscountPercent', undefined, discountPercent);
		}
	},

	[globalRelay.JOB_COST_UPDATE]: (jobCost, summary, updatePath) => {
		if (updatePath === 'margin') {
			const storeJobCost = getModel({ collection: ModelCollection.jobCosts, key: jobCost.key });

			if (storeJobCost) {
				const oldMargin = storeJobCost.margin;
				_.forEach(jobCost.lineItems, item => {
					if (item.margin === oldMargin) {
						item.margin = jobCost.margin;
						globalRelay.triggerJobCostLineUpdate(item, 'margin', jobCost);
					}
				});

				if (jobCost.installationMargin === oldMargin) {
					jobCost.installationMargin = jobCost.margin;
				}

				_.forEach(getInstallSheets(jobCost), item => {
					if (item.margin === oldMargin) {
						item.margin = jobCost.margin;
					}
				});
			}
			else {
				jobCost.installationMargin = jobCost.margin;
			}
		}
		else if (updatePath === 'name') {
			// be sure not to create a duplicate name
			ensureUniqueJobCostName(jobCost);
		}

		if (jobCost.allowedContractEdits !== 'none') {
			const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);

			if (jobCost.installationMode !== 'none') {
				// DR - this is temporarily disabled as legacy moves to a significantly different installation estimating model and wants to only manually update the config. This will be re-enabled at some point in the future.
				//jobCost.setSetting(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined, globalActions.formatInstallationConfigName(jobCost));
			}

			const workAreaSpecVersion = globalConfig.jobCostSettings.specifications.workAreaSpecVersion;
			const workAreaConfigName = globalConfig.jobCostSettings.specifications.useVariantWorkAreaConfig ? `workarea-specs-${jobCost.companyVariant}-${workAreaSpecVersion}` : `workarea-specs-${workAreaSpecVersion}`;
			jobCost.setSetting(enums.jobCostSettingGroups.General, 'workAreaSpecDefinition', undefined, workAreaConfigName);
			jobCost.setSetting(enums.jobCostSettingGroups.General, 'deliveryConfigVersion', undefined, globalConfig.jobCostSettings.deliveryConfigVersion);
		}
	},
	[globalRelay.JOB_COST_LINE_UPDATE]: (lineItem, updatePath, jobCost, deleted) => {
		if (deleted) { return; }

		const storeInstance = store();
		const job = getModel({ store: storeInstance, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const currentModule = storeInstance.getIn(['viewState', 'currentModule']);

		// handle cost, sell, factor and margin settings
		if (['client-upgrade', 'builder-upgrade'].indexOf(lineItem.type) !== -1) {
			// be sure that cost is always 0 and margin is always 1
			lineItem.cost = 0;
			lineItem.margin = 1;
		}
		else if (lineItem.type === 'subtrade') {
			if (updatePath === 'cost' || updatePath === 'costFactor') {
				lineItem.sellPrice = ((lineItem.costFactor * lineItem.cost) + lineItem.cost).toFixed(2);
			}
			else if (updatePath === 'sellPrice') {
				lineItem.costFactor = ((lineItem.sellPrice - lineItem.cost) / lineItem.cost).toFixed(4);
			}
		}
		else {
			if (['cost', 'freight', 'margin', 'quantity'].indexOf(updatePath) !== -1) { // eslint-disable-line no-lonely-if
				lineItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(lineItem.totalCost, lineItem.margin).toFixed(2));
			}
			else if (updatePath === 'sellPrice') {
				lineItem.margin = Number(util.costCalculator.getMarginWithCostAndSell(lineItem.totalCost, lineItem.sellPrice).toFixed(4));
			}
		}

		// Accessory & Mods specific rules
		if (['outside-supplier', 'warehouse'].indexOf(lineItem.type) !== -1) {
			if (updatePath === 'productName' && lineItem.productKey) {
				const vendor = getModel({ store: storeInstance, collection: ModelCollection.vendors, key: lineItem.vendorKey });
				const product = vendor && vendor.products[lineItem.productKey];
				if (product) {
					lineItem.cost = product.cost.toFixed(2);
				}
				else {
					lineItem.productKey = undefined;
					lineItem.cost = 0;
				}
				lineItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(lineItem.totalCost, lineItem.margin));
			}
		}

		// Cabinet specific rules
		if (lineItem.type === 'cabinet') {
			const vendor = getModel({ store: storeInstance, collection: ModelCollection.vendors, key: lineItem.vendorKey });
			if (updatePath === 'listPrice' || updatePath === 'costFactor') {
				lineItem.cost = (lineItem.costFactor * lineItem.listPrice).toFixed(2);

				// be sure this is calculated before sellPrice so that totalCost is correctly reflected
				if (vendor && vendor.freightFactor) {
					lineItem.freight = lineItem.cost * vendor.freightFactor;
				}

				lineItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(lineItem.totalCost, lineItem.margin).toFixed(2));
			}

			// write this every time a cabinet line is updated to be sure the summary calculates with the most recent value
			if (vendor && (job || ['retail-sales', 'builder-sales'].indexOf(currentModule) !== -1)) {
				const businessSegment = job ? job.businessSegment : enums.businessSegments[_.startCase(currentModule.split('-')[0])];
				const overheadFactor = businessSegment === enums.businessSegments.Builder ? vendor.overheadFactorBuilder : vendor.overheadFactorRetail;

				jobCost.setVendorOverheadFactor(vendor.key, overheadFactor);
			}
		}

		// Glass specific rules
		if (lineItem.type === 'glass') {
			const glassTypes = getDomainEntities({ store: storeInstance, type: DomainEntityType.glassTypes });
			const doorSizes = getDomainEntities({ store: storeInstance, type: DomainEntityType.doorSizes });

			if (updatePath === 'glassTypeKey' && lineItem.glassTypeKey) {
				const glassType = glassTypes[lineItem.glassTypeKey];
				const totalUnits = lineItem.doorSizeKey ? doorSizes[lineItem.doorSizeKey].totalUnits : 0;
				lineItem.cost = (totalUnits > 0) ? (glassType.costPerUnit * totalUnits) : 0;
				lineItem.sellPrice = (lineItem.margin > 0) ? Number(util.costCalculator.getSellWithCostAndMargin((lineItem.totalCost), lineItem.margin).toFixed(2)) : 0;
			}
			else if (updatePath === 'doorSizeKey' && lineItem.doorSizeKey) {
				const doorSize = doorSizes[lineItem.doorSizeKey];
				lineItem.unit = doorSize.unitName;
				lineItem.cost = lineItem.glassTypeKey ? (doorSize.totalUnits * glassTypes[lineItem.glassTypeKey].costPerUnit) : 0;
				lineItem.sellPrice = (lineItem.margin > 0) ? Number(util.costCalculator.getSellWithCostAndMargin((lineItem.totalCost), lineItem.margin).toFixed(2)) : 0;
			}
		}

		// Custom installation specific rules
		if (lineItem.type === 'installation-custom') {
			const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);

			if (updatePath === 'unit') {
				const unitOptions = globalConfig.jobCostSettings.installation.customUnits;
				const unitOption = _.find(unitOptions, { value: lineItem.unit });

				if (unitOption) {
					lineItem.cost = unitOption.defaultCost;
					lineItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(lineItem.totalCost, lineItem.margin).toFixed(2));
				}
			}
		}

		// Update delivery cost if appropriate
		if ((lineItem.isCabinetCount && updatePath === 'quantity') || (lineItem.type === 'delivery' && updatePath === 'pieceCount')) {
			const deliveryLineItem = jobCost.withStrictFilters(strictJobCost => {
				return (lineItem.type === 'delivery') ? lineItem : strictJobCost.getLineItemsByType('delivery');
			});

			if (deliveryLineItem) {
				deliveryLineItem.pieceCount = (lineItem.type === 'delivery') ? deliveryLineItem.pieceCount : Math.ceil(lineItem.quantity);

				const deliveryConfigVersion = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'deliveryConfigVersion', undefined, 'v1');
				const deliveryConfig = storeInstance.getIn(['model', 'config', _.camelCase(`delivery-${deliveryConfigVersion}`)]);

				if (deliveryConfig !== undefined) {
					let option;
					let additionalCost = 0;
					if (deliveryLineItem.pieceCount) {
						_.forEach(deliveryConfig.Retail.pieceRanges, range => {
							if (deliveryLineItem.pieceCount >= range.min && deliveryLineItem.pieceCount <= range.max) {
								additionalCost = Number(range.additionalCost);
								option = _.find(deliveryConfig.Retail.options, { name: deliveryLineItem.orderNote || '~' });
								return false;
							}
						});
					}

					if (option) {
						deliveryLineItem.cost = Number(option.baseCost);
						if (!option.disableRangeAddition) {
							deliveryLineItem.cost += additionalCost;
						}
						deliveryLineItem.sellPrice = deliveryConfig.Retail.doNotMargin ? deliveryLineItem.totalCost : Number(util.costCalculator.getSellWithCostAndMargin(deliveryLineItem.totalCost, deliveryLineItem.margin).toFixed(2));
					}

					if (!deliveryLineItem.sellPrice && !deliveryLineItem.pieceCount && !deliveryLineItem.orderNote) {
						jobCost.lineItems.delete(deliveryLineItem.key);
					}
				}
			}
		}
	}
};

// wrap the handlers to allow extensions to modify the globalHandlers implementation
for (const key of _.keys(globalHandlers)) {
	globalRelay.on(key, (...args) => {
		(globalHandlers[key] as any)(...args);
	});
}

export const fetchJobCost = params => {
	return serviceGateway.fetchJobCost(params)
		.then(result => {
			if (result.jobCost) {
				store().setIn(['model', 'jobCosts', result.jobCost.key], jobCostModel(result.jobCost));
			}
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchJobCosts = params => {
	return serviceGateway.fetchJobCosts(params)
		.then(result => {
			const jobCostsCollection = getCollection({ collection: ModelCollection.jobCosts });

			jobCostsCollection.withMutations(c => {
				_.forEach(result.jobCosts, jc => {
					c.set(jc.key, jobCostModel(jc));
				});
			});
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const utilFetch = params => {
	return serviceGateway.jobCostUtil(params)
		.then(result => {
			return _.map(result.jobCosts, jc => {
				return jobCostModel(jc);
			});
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const deleteJobCost = params => {
	const keyPath = ['model', 'jobCosts', params.jobCost.key];
	const alteredJobs:any[] = [];

	store().deleteIn(keyPath);
	return serviceGateway.deleteJobCost(params)
		.then(() => {
			const model = store().get('model');

			model.withMutations(map => {
				const jobs = map.get('jobs');

				jobs.forEach(j => {
					const removed = _.remove(j.jobCostKeys, k => k === params.jobCost.key);
					if (removed.length) {
						alteredJobs.push(j);
					}
				});
			});
		})
		.catch(err => {
			const model = store().get('model');

			model.withMutations(map => {
				_.forEach(alteredJobs, j => {
					const job = map.getIn(['jobs', j.key]);
					job.jobCostKeys.push(params.jobCost.key);
				});

				store().setIn(keyPath.slice(1), params.jobCost);
			});
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const saveJobCost = params => {
	const keyPath = ['model', 'jobCosts', params.jobCost.key];

	store().setIn(keyPath, jobCostModel(params.jobCost));
	let jobKeyPath;

	// If there is a jobKey, then we need to add the jobcost key
	// to the job and update the store.
	if (params.jobCost.jobKey) {
		jobKeyPath = ['model', 'jobs', params.jobCost.jobKey];
		const job = store().getIn(jobKeyPath);

		if (job) {
			job.jobCostKeys.push(params.jobCost.key);

			store().setIn(jobKeyPath, jobModel(job));
		}
	}

	return serviceGateway.saveJobCost({ jobCost: params.jobCost, patch: params.jobCost.dateCreated ? params.patch : undefined })
		.then(result => {
			store().setIn(keyPath, jobCostModel(result.jobCost));
		})
		.catch(err => {
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return saveJobCost(p || params);
			};

			if (jobKeyPath) {
				const job = store().getIn(jobKeyPath);

				if (job) {
					_.remove(job.jobCostKeys, f => {
						return f === params.jobCost.key;
					});

					store().setIn(jobKeyPath, jobModel(job));
				}
			}

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'saveJobCost',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};